// TMT(순서 잇기) 스픽(Spick) 버전 — 게임 설정 및 로직
// 신한 SOL 버전과 동일한 3-Stage 규칙을 사용한다.
// Stage 1: 숫자·한글 교대 순서 잇기 (6노드)
// Stage 2: 색상 조건 + 순서 잇기 (8노드, 방해 노드 포함)
// Stage 3: 색상+도형 조건 + 순서 잇기 (8노드, 방해 노드 포함)

export const TMT_GAME_NAME = '순서 잇기';
export const TMT_COGNITIVE_TAG = '개념사고';

// ── 보드 좌표 공간 (SVG viewBox) ──
export const BOARD_W = 400;
export const BOARD_H_PRACTICE1 = 440;
export const BOARD_H_PRACTICE2 = 440;
export const BOARD_H_STAGE1 = 520;
export const BOARD_H_STAGE2 = 560;
export const BOARD_H_STAGE3 = 560;

export interface NodePosition {
  x: number;
  y: number;
}

// ── 노드 시각 속성 (Stage 2/3 조건용) ──
export type NodeShape = 'square' | 'circle' | 'triangle';
export type NodeColor = 'yellow' | 'blue' | 'green';

export interface TmtNodeDef extends NodePosition {
  id: string;
  label?: string;
  shape?: NodeShape;
  color?: NodeColor;
}

// ── 연습 1: 기본 연결 (1 → 가 → 2 → 나) ──
export const PRACTICE1_ORDER: string[] = ['1', '가', '2', '나'];
export const PRACTICE1_NODES: TmtNodeDef[] = [
  { id: '1', label: '1', x: 60, y: 120 },
  { id: '가', label: '가', x: 320, y: 100 },
  { id: '2', label: '2', x: 90, y: 320 },
  { id: '나', label: '나', x: 320, y: 340 },
];

// ── 연습 2: 조건 연결 (노란 네모 가 → 나 + 방해 노드) ──
export const PRACTICE2_ORDER: string[] = ['가', '나'];
export const PRACTICE2_RULE_COLOR: NodeColor = 'yellow';
export const PRACTICE2_RULE_SHAPE: NodeShape = 'square';
export const PRACTICE2_NODES: TmtNodeDef[] = [
  // 정답 (노란 네모)
  { id: '가', label: '가', shape: 'square', color: 'yellow', x: 60, y: 120 },
  { id: '나', label: '나', shape: 'square', color: 'yellow', x: 320, y: 100 },
  // 방해 (속성 하나만 다름)
  { id: '가_파', label: '가', shape: 'square', color: 'blue', x: 90, y: 320 },
  { id: '나_원', label: '나', shape: 'circle', color: 'yellow', x: 320, y: 340 },
];

// ── Stage 1: 기본 순서 (1 → 가 → 2 → 나 → 3 → 다) ──
export const STAGE1_ORDER: string[] = ['1', '가', '2', '나', '3', '다'];
export const STAGE1_NODES: TmtNodeDef[] = [
  { id: '1', label: '1', x: 55, y: 90 },
  { id: '가', label: '가', x: 325, y: 75 },
  { id: '2', label: '2', x: 80, y: 260 },
  { id: '나', label: '나', x: 320, y: 245 },
  { id: '3', label: '3', x: 205, y: 400 },
  { id: '다', label: '다', x: 60, y: 465 },
];

// ── Stage 2: 색상 조건 + 순서 (노란색 1 → 2 → 3 → 4) ──
export const STAGE2_RULE_COLOR: NodeColor = 'yellow';
export const STAGE2_ORDER: string[] = ['1', '2', '3', '4'];
export const STAGE2_NODES: TmtNodeDef[] = [
  // 정답 (노란색)
  { id: '1', label: '1', color: 'yellow', x: 55, y: 80 },
  { id: '2', label: '2', color: 'yellow', x: 325, y: 75 },
  { id: '3', label: '3', color: 'yellow', x: 335, y: 210 },
  { id: '4', label: '4', color: 'yellow', x: 65, y: 420 },
  // 방해 (색상만 다름)
  { id: '1_파', label: '1', color: 'blue', x: 200, y: 55 },
  { id: '2_초', label: '2', color: 'green', x: 85, y: 245 },
  { id: '3_파', label: '3', color: 'blue', x: 195, y: 290 },
  { id: '4_초', label: '4', color: 'green', x: 300, y: 415 },
];

// ── Stage 3: 색상+도형 조건 + 순서 (노란 네모 가 → 나 → 다 → 라) ──
export const STAGE3_RULE_COLOR: NodeColor = 'yellow';
export const STAGE3_RULE_SHAPE: NodeShape = 'square';
export const STAGE3_ORDER: string[] = ['가', '나', '다', '라'];
export const STAGE3_NODES: TmtNodeDef[] = [
  // 정답 (노란 네모)
  { id: '가', label: '가', shape: 'square', color: 'yellow', x: 55, y: 90 },
  { id: '나', label: '나', shape: 'square', color: 'yellow', x: 325, y: 75 },
  { id: '다', label: '다', shape: 'square', color: 'yellow', x: 335, y: 215 },
  { id: '라', label: '라', shape: 'square', color: 'yellow', x: 65, y: 425 },
  // 방해 (색상 또는 도형 하나만 다름)
  { id: '가_원', label: '가', shape: 'circle', color: 'yellow', x: 200, y: 55 },
  { id: '나_파', label: '나', shape: 'square', color: 'blue', x: 85, y: 245 },
  { id: '다_세', label: '다', shape: 'triangle', color: 'yellow', x: 195, y: 290 },
  { id: '라_파', label: '라', shape: 'square', color: 'blue', x: 300, y: 415 },
];

// 노드 색상 → Tailwind 클래스 (기능적 조건색)
export const NODE_COLOR_CLASS: Record<NodeColor, string> = {
  yellow: 'bg-amber-400 text-amber-950',
  blue: 'bg-blue-500 text-white',
  green: 'bg-green-500 text-white',
};

// ── 점수 기준값 (프로토타입 밸런싱 임시값 — 플레이 데이터 확인 후 조정) ──
export const TMT_ERROR_PENALTY = 3; // 오답 1회당 가산 시간(초)
export const TMT_MIN_SCORE = 20;
export const TMT_MAX_SCORE = 100;

export const TMT_STAGE1_MAX_SCORE = 25;
export const TMT_STAGE1_FAST_TIME = 10;
export const TMT_STAGE1_SLOW_TIME = 30;
export const TMT_STAGE1_MAX_TIME = 30;
export const TMT_STAGE1_MAX_ERRORS = 3;

export const TMT_STAGE2_MAX_SCORE = 30;
export const TMT_STAGE2_FAST_TIME = 15;
export const TMT_STAGE2_SLOW_TIME = 40;
export const TMT_STAGE2_MAX_TIME = 40;
export const TMT_STAGE2_MAX_ERRORS = 4;

export const TMT_STAGE3_MAX_SCORE = 45;
export const TMT_STAGE3_FAST_TIME = 20;
export const TMT_STAGE3_SLOW_TIME = 50;
export const TMT_STAGE3_MAX_TIME = 50;
export const TMT_STAGE3_MAX_ERRORS = 4;

// 완성 연출 / 중립 전환 지연(ms)
// 3개 단계가 하나의 45초 타이머로 이어지므로, 완성 피드백은 아주 짧게 보여주고
// 곧바로 다음 단계로 넘어간다. (DSST처럼 정답 처리 후 바로 다음으로 진행)
export const TMT_TOTAL_DURATION_SEC = 45;
export const TMT_COMPLETE_DELAY_MS = 380;
export const TMT_TRANSITION_DELAY_MS = 700;

// 오답으로 단계를 넘길 때 보여주는 안내 문구 노출 시간(ms)
export const TMT_NOTICE_MS = 1400;

// 드래그 연결 시 노드 터치 판정 영역 배율 (노년층 사용자 배려)
export const TMT_HIT_SCALE = 1.6;

// ── Stage/연습 공통 설정 ──
export interface TmtStageConfig {
  stageLabel: string;
  ruleHint: string;
  ruleColor?: NodeColor;
  ruleShape?: NodeShape;
  nodes: TmtNodeDef[];
  order: string[];
  boardH: number;
  nodeSize: number;
  maxTimeSec: number;
  maxErrors: number;
  maxScore: number;
  fastTime: number;
  slowTime: number;
  completeMsg: string;
}

export const PRACTICE1_CONFIG: TmtStageConfig = {
  stageLabel: '연습 1 · 기본 연결',
  ruleHint: '1 → 가 → 2 → 나 순서로 이어보세요',
  nodes: PRACTICE1_NODES,
  order: PRACTICE1_ORDER,
  boardH: BOARD_H_PRACTICE1,
  nodeSize: 56,
  maxTimeSec: 9999,
  maxErrors: 9999,
  maxScore: 0,
  fastTime: 0,
  slowTime: 0,
  completeMsg: '좋아요!',
};

export const PRACTICE2_CONFIG: TmtStageConfig = {
  stageLabel: '연습 2 · 조건 연결',
  ruleHint: '노란 네모를 찾아 가 → 나 순서로 이어보세요',
  ruleColor: PRACTICE2_RULE_COLOR,
  ruleShape: PRACTICE2_RULE_SHAPE,
  nodes: PRACTICE2_NODES,
  order: PRACTICE2_ORDER,
  boardH: BOARD_H_PRACTICE2,
  nodeSize: 56,
  maxTimeSec: 9999,
  maxErrors: 9999,
  maxScore: 0,
  fastTime: 0,
  slowTime: 0,
  completeMsg: '좋아요!',
};

export const STAGE1_CONFIG: TmtStageConfig = {
  stageLabel: '1단계 · 기본 순서',
  ruleHint: '1 → 가 → 2 → 나 → 3 → 다',
  nodes: STAGE1_NODES,
  order: STAGE1_ORDER,
  boardH: BOARD_H_STAGE1,
  nodeSize: 56,
  maxTimeSec: TMT_STAGE1_MAX_TIME,
  maxErrors: TMT_STAGE1_MAX_ERRORS,
  maxScore: TMT_STAGE1_MAX_SCORE,
  fastTime: TMT_STAGE1_FAST_TIME,
  slowTime: TMT_STAGE1_SLOW_TIME,
  completeMsg: '좋아요!',
};

export const STAGE2_CONFIG: TmtStageConfig = {
  stageLabel: '2단계 · 색 찾기',
  ruleHint: '노란색을 찾아 1 → 2 → 3 → 4',
  ruleColor: STAGE2_RULE_COLOR,
  nodes: STAGE2_NODES,
  order: STAGE2_ORDER,
  boardH: BOARD_H_STAGE2,
  nodeSize: 54,
  maxTimeSec: TMT_STAGE2_MAX_TIME,
  maxErrors: TMT_STAGE2_MAX_ERRORS,
  maxScore: TMT_STAGE2_MAX_SCORE,
  fastTime: TMT_STAGE2_FAST_TIME,
  slowTime: TMT_STAGE2_SLOW_TIME,
  completeMsg: '좋아요!',
};

export const STAGE3_CONFIG: TmtStageConfig = {
  stageLabel: '3단계 · 모양과 색 찾기',
  ruleHint: '노란색 네모를 찾아 가 → 나 → 다 → 라',
  ruleColor: STAGE3_RULE_COLOR,
  ruleShape: STAGE3_RULE_SHAPE,
  nodes: STAGE3_NODES,
  order: STAGE3_ORDER,
  boardH: BOARD_H_STAGE3,
  nodeSize: 54,
  maxTimeSec: TMT_STAGE3_MAX_TIME,
  maxErrors: TMT_STAGE3_MAX_ERRORS,
  maxScore: TMT_STAGE3_MAX_SCORE,
  fastTime: TMT_STAGE3_FAST_TIME,
  slowTime: TMT_STAGE3_SLOW_TIME,
  completeMsg: '완성!',
};

// ── 상태 / 데이터 타입 ──
export type CompletionStatus = 'completed' | 'time_limit' | 'error_limit';

// 개별 노드 선택 기록
export interface TmtNodeSelection {
  nodeId: string;
  isCorrect: boolean;
  selectedAtMs: number;
  moveTimeMs: number;
}

// Stage 2/3 오답 분류 기록 (색상/도형/순서 오류 여부)
export interface TmtWrongRecord {
  nodeId: string;
  colorError: boolean;
  shapeError: boolean;
  orderError: boolean;
}

// Stage별 종료 데이터
export interface TmtStageData {
  stage: 'stage1' | 'stage2' | 'stage3';
  completionStatus: CompletionStatus;
  timeMs: number;
  adjustedTimeSec: number;
  wrongCount: number;
  completedCorrectCount: number;
  totalCorrectCount: number;
  selections: TmtNodeSelection[];
  wrongSelections: string[];
  wrongDetail: TmtWrongRecord[];
  lastCorrectNode: string | null;
  endedAt: string;
  stageScore: number;
}

// 전체 게임 수집 데이터
export interface TmtGameData {
  gameType: string;
  performedAt: string;
  stage1: TmtStageData | null;
  stage2: TmtStageData | null;
  stage3: TmtStageData | null;
  totalTimeMs: number;
  totalWrongCount: number;
  completedStageCount: number;
  stageScores: { stage1: number; stage2: number; stage3: number };
  score: number;
  isAborted: boolean;
}

function clamp(v: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, v));
}

function roundToNearest5(v: number): number {
  return Math.round(v / 5) * 5;
}

// Stage별 점수 계산
// 정상 완료: 보정시간을 FastTime~SlowTime 사이 선형 환산 (0 ~ maxScore)
// 미완료: maxScore × (연결 노드 / 전체 노드) × 0.5 (최대 50%)
export function calculateStageScore(
  maxScore: number,
  fastTime: number,
  slowTime: number,
  adjustedTimeSec: number,
  completedCorrectCount: number,
  totalCorrectCount: number,
  completed: boolean,
): number {
  if (completed) {
    const ratio = clamp((adjustedTimeSec - fastTime) / (slowTime - fastTime), 0, 1);
    return Math.round(maxScore * (1 - ratio) * 10) / 10;
  }
  const progress = totalCorrectCount > 0 ? completedCorrectCount / totalCorrectCount : 0;
  return Math.round(maxScore * progress * 0.5 * 10) / 10;
}

// 최종 게임점수: Stage별 점수 합산 → 5점 반올림 → 20~100 clamp
export function calculateFinalScore(stageScores: number[]): number {
  const rawTotal = stageScores.reduce((sum, s) => sum + s, 0);
  return clamp(roundToNearest5(rawTotal), TMT_MIN_SCORE, TMT_MAX_SCORE);
}

// Stage 2/3 오답 분류 (색상/도형/순서 오류 여부)
export function classifyWrong(
  nodeId: string,
  ruleColor: NodeColor | undefined,
  ruleShape: NodeShape | undefined,
  nodes: TmtNodeDef[],
): TmtWrongRecord {
  const node = nodes.find((n) => n.id === nodeId);
  if (!node) return { nodeId, colorError: false, shapeError: false, orderError: false };
  const colorError = !!ruleColor && node.color !== ruleColor;
  const shapeError = !!ruleShape && node.shape !== ruleShape;
  const orderError = !colorError && !shapeError;
  return { nodeId, colorError, shapeError, orderError };
}

// 결과 안내 문구 (이번 수행에 대한 긍정 피드백만, 인지기능 판정 표현 금지)
export function getResultComment(score: number): string {
  if (score >= 90) return '빠르고 정확하게 잘 이어주셨어요!';
  if (score >= 70) return '좋은 흐름으로 잘 이어주셨어요!';
  if (score >= 50) return '차근차근 잘 이어주셨어요!';
  return '끝까지 잘 참여하셨어요!';
}